// ./src/hooks/useCreateGate.ts
//
// What a LIST PAGE needs from the plan gate, in one call: may I add another, what do I say
// if not, and where does the button go instead.
//
// ⚠ CREATE only, because that is where the server gates. 22 of the 23 gated cores put
// `planFeatureGate` in `beforeCreate` and nowhere else, so reads, updates and removes are
// always allowed — gating more here would lock a host out of data the API is still serving
// them, and out of edits it still accepts.
//
// ⚠ THE ONE EXCEPTION IS `EventCore`, which gates in `beforeUpdate` — turning approval or the
// waitlist ON (`pro_registration`) and attaching a video cover (`rich_media`). Deliberately,
// and only in the ON direction: a host whose plan lapsed must still be able to switch those
// OFF, or the event is stranded in a state it cannot leave. That is a per-CONTROL lock, not a
// per-page one, so it uses `useFeatureGate().checkFeature` directly rather than this hook.
//
// The verdict comes from `useFeatureGate`, which is the only thing allowed to resolve
// entitlement (event grant OR account plan) — see that file.
import { useMemo } from "react";

import { FeatureGateScope, useFeatureGate } from "./useFeatureGate";
import { EntitlementVerdict } from "@/utils/planEntitlement";

export interface UseCreateGateReturn {
  /** The verdict. Hand it straight to `<PlanFeatureNotice>`. */
  verdict: EntitlementVerdict;
  /**
   * May the host create another one right now?
   *
   * ⚠ Check this INSIDE the page's own add handler, not on each button. Every gated page has
   * three or four ways into the same drawer — the header action, ⌘I, the ⌘K command, the
   * empty state's Add, sometimes a click on the canvas — and a guard on three of four is a
   * guard on none.
   */
  canCreate: boolean;
  /**
   * Tooltip/label for the Add control when it is blocked — the same sentence the server
   * would have returned. `null` when creating is allowed.
   */
  blockedTooltip: string | null;
  /** "Lock" when blocked, so the header control reads as locked rather than merely broken. */
  addIconName: string;
  goToUpgrade: () => void;
}

/**
 * @param featureCode the feature the server checks on create (see the core's `planFeatureGate`)
 * @param scope       `eventId` when the action belongs to an event, and `currentCount` for a
 *                    NUMERIC ceiling — the count BEFORE adding, matching the server contract; `sample` for a
 *                    back-office tool (#238 — a draft tries `PLAN_TRIAL_SAMPLE` of it; pass a module-level noun).
 */
export const useCreateGate = (featureCode: string, scope?: FeatureGateScope): UseCreateGateReturn => {
  const { checkFeature, goToUpgrade } = useFeatureGate();

  const eventId = scope?.eventId ?? null;
  const currentCount = scope?.currentCount ?? null;
  // #238 — a back-office tool's sample on a draft: the count of what the EVENT has (the server's), not what is loaded.
  const sampleCount = scope?.sample?.count ?? null;
  const sampleNoun = scope?.sample?.noun ?? null;
  const samplePublishAsks = scope?.sample?.publishAsks === true;

  const verdict = useMemo(
    () =>
      checkFeature(featureCode, {
        eventId,
        currentCount,
        sample: sampleNoun ? { count: sampleCount, noun: sampleNoun, publishAsks: samplePublishAsks } : undefined,
      }),
    [checkFeature, featureCode, eventId, currentCount, sampleCount, sampleNoun, samplePublishAsks]
  );

  return useMemo(
    () => ({
      verdict,
      canCreate: verdict.allowed,
      blockedTooltip: verdict.allowed ? null : verdict.message,
      addIconName: verdict.allowed ? "Plus" : "Lock",
      goToUpgrade,
    }),
    [verdict, goToUpgrade]
  );
};
